import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@mui/styles';
import withRouter from '../../components/App/hooks/with-router';
import { lightGray, white } from '../../styles/style-constants';

import Footer from '../../components/@common/PageFooter';
import RouteTabMenu from '../../components/Resources/RouteTabMenu/RouteTabMenu';
import AllResources from './containers/AllResources';
import MyStuff from './containers/MyStuff';
import Clickaways from './containers/Clickaways';
import ResourcesFilter from '../../components/Resources/ResourcesFilter';

import AllResourcesIcon from 'assets/icons/AllResourcesIcon';
import MyStuffIcon from 'assets/icons/PinIcon';
import ClickAwayIcon from 'assets/icons/ClickAwayIcon';
import ExpiredLicenseAlert from '../../components/@common/ExpiredLicenseAlert/__tests__/ExpiredLicenseAlert';
import withFtueTour from '../../components/@common/enhancers/withFtueTour';
import {
  toggleClickAwayAlertDisplay,
  isShowClickAwayAlert,
  showOnboardingModal
} from 'state/modules/messaging';
import { translate } from '../../translations/translator';

import { AppStoreState } from 'types';
import { UserState } from 'state/modules/user';
import { ComponentType } from 'react';
import { RouteTabMenuItem } from '../../components/Resources/RouteTabMenu/RouteTabMenu';
import { getLicenseIsTrial, hasClickAwayOnly } from 'state/modules/user/selectors/user-selectors';
import { getTrialDaysRemaining } from 'state/modules/user/util';
import { setResourceFilters } from 'state/modules/app';
import { toggleSlideoutMenu } from 'state/modules/ui/actions';
import { Box } from '@mui/material';

const translateResources = key => translate('resources', key);

export type ResourcesSubRoute = {
  label: string;
  endLink: string;
  component: any;
  icon: ComponentType<any>;
  exact?: boolean;
  hasBadge?: boolean;
};

type Props = {
  counter: number;
  isClickAway: boolean;
  isShowClickAwayAlert: boolean;
  isTrialLicense: boolean;
  locale: string;
  router: any;
  setFilters: any;
  showOnboardingModal: typeof showOnboardingModal;
  textColor: string;
  themeColor: string;
  trialDaysLeft: number;
  user: UserState | any;
  setSearchFavoritesFilter: (searchText: string) => void;
  toggleClickAwayAlertDisplay: typeof toggleClickAwayAlertDisplay;
  toggleSlideoutMenu: typeof toggleSlideoutMenu;
  classes?: any;
};

class Resources extends Component<Props> {
  private tabMenuItems: Array<RouteTabMenuItem>;

  constructor(props: Props | any) {
    super(props);
    this.tabMenuItems = this.getTabMenuItems(props);
  }

  componentDidUpdate(prevProps: Props) {
    this.tabMenuItems = this.getTabMenuItems(this.props);
    if (prevProps.locale !== this.props.locale) {
      this.resetFilters();
    }
  }

  resetFilters() {
    this.props.setFilters('', []);
  }
  getTabLink(fullUrl: string, endLink: string): string {
    return fullUrl.split('resources')[0] + 'resources' + endLink;
  }

  getTabMenuItems = (props: Props): Array<RouteTabMenuItem> => {
    const { pathname }: { pathname: string } = props.router.location;
    const resourcesSubRoutes: any =
      //check both in case they change the URL and try to force into the full resources path
      //but the props are not always updated to reflect Click Away by the time this gets called
      props.isClickAway || pathname.includes('clickaways')
        ? //Clickaway only routes
          [
            {
              label: translateResources('clickAway'),
              endLink: '/clickaways',
              component: Clickaways,
              icon: ClickAwayIcon
            },
            {
              label: translateResources('pinnedAClickAways'),
              endLink: '/pinned-clickaways',
              icon: MyStuffIcon,
              component: MyStuff,
              hasBadge: true
            }
          ]
        : //Normal routes
          [
            {
              label: translateResources('allResources'),
              endLink: '/',
              component: AllResources,
              icon: AllResourcesIcon
            },
            {
              label: translateResources('pinnedResources'),
              endLink: '/pinned-resources',
              icon: MyStuffIcon,
              component: MyStuff,
              hasBadge: true
            }
          ];

    return resourcesSubRoutes.map((item: any): any => {
      return {
        label: item.label,
        link: this.getTabLink(pathname, item.endLink),
        paramPath: this.getTabLink(pathname, item.endLink),
        component: item.component,
        icon: item.icon,
        hasBadge: item.hasBadge
      };
    });
  };

  handleCloseAlert = (): void => {
    this.props.toggleClickAwayAlertDisplay(this.props.user.licenses[0].productCurriculumId, false);
  };

  getContent = () => {
    const url = this.props.router.location.pathname;
    if (this.props.isClickAway) {
      if (url.includes('pinned-clickaways')) return <MyStuff />;
      return <Clickaways />;
    }
    if (url.includes('pinned-resources')) return <MyStuff />;
    return <AllResources />;
  };

  render(): React.ReactElement {
    const {
      counter,
      router: { location },
      isShowClickAwayAlert,
      isTrialLicense,
      trialDaysLeft,
      isClickAway,
      classes: { resources, content, subRouteContainer }
    } = this.props;

    return (
      <Box sx={resources}>
        <ExpiredLicenseAlert show={isShowClickAwayAlert} onClose={this.handleCloseAlert} />
        <RouteTabMenu
          overflowUnderline
          items={this.tabMenuItems}
          activeRoute={location.pathname}
          counter={counter}
        />
        {!isClickAway && <ResourcesFilter />}
        <Box sx={content}>
          <Box sx={subRouteContainer}>{this.getContent()}</Box>
        </Box>
        <Footer
          isTrialLicense={isTrialLicense}
          trialDaysLeft={trialDaysLeft}
          showTrialModal={null}
        />
      </Box>
    );
  }
}

const mapStateToProps = (state: AppStoreState, { router: { params } }: Props) => ({
  counter: state.app.counter,
  isShowClickAwayAlert: isShowClickAwayAlert(state, params),
  isTrialLicense: getLicenseIsTrial(state, params),
  locale: state.app.locale,
  trialDaysLeft: getTrialDaysRemaining(state.user),
  user: state.user,
  workbook: state.app.currentWorkbook,
  isClickAway: hasClickAwayOnly(state, params)
});

const styles: any = {
  resources: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: white,
    overflowY: 'auto'
  },
  content: {
    backgroundColor: lightGray,
    borderTop: '1px solid #B0B0B0',
    width: '100%',
    height: '100%',
    overflow: 'auto'
  },
  subRouteContainer: {
    flex: 1
  }
};

export default compose(
  withRouter,
  withFtueTour('resources', 'resourceFilters'),
  withStyles(styles),
  connect(mapStateToProps, {
    setFilters: setResourceFilters,
    showOnboardingModal,
    toggleClickAwayAlertDisplay,
    toggleSlideoutMenu
  })
)(Resources);
